import {
  CUSTOM_ERROR_MIMETYPE,
  DISPLAY_TABLE_MIMETYPE,
  DisplayTableOutput,
  DistinctColumnValues,
  FILLED_DYNAMIC_VALUE_MIMETYPE,
  FilledDynamicValue,
  HEX_PRIVATE_PREFIX,
  MAP_FILLED_DYNAMIC_VALUE_MIMETYPE,
  OutputId,
  OutputType,
  SQL_COMPILED_QUERY_MIMETYPE,
  SQL_STATUS_MIMETYPE,
  SqlCompiledQuery,
  SqlStatus,
  notEmpty,
  parseFilledDynamicValues,
} from "@hex/common";
import { useMemo } from "react";

import { getTableOutputs } from "../components/cell/renderers/query-cells-shared/utils.js";
import { getMostRecentMetadataOutputContent } from "../components/output/metadataOutputs";
import { UseCellDataResult } from "../components/output/useCellData";
import { OutputMPWithResolvedContent } from "../redux/slices/appSessionMPSlice.js";

import { logErrorMsg, logInfoMsg } from "./logging";

export function getCompiledSqlOutput(
  outputs: UseCellDataResult[],
): SqlCompiledQuery[] {
  return outputs
    .filter((o) =>
      o.outputFragment.frontendOutputContents.some(
        (oc) => oc.mimeType === SQL_COMPILED_QUERY_MIMETYPE,
      ),
    )
    .map((o) => {
      const compiledQueryOutput = o.outputFragment.frontendOutputContents.find(
        (oc) => oc.mimeType === SQL_COMPILED_QUERY_MIMETYPE,
      );
      if (compiledQueryOutput == null) {
        return null;
      }

      try {
        return SqlCompiledQuery.check(
          JSON.parse(JSON.parse(compiledQueryOutput.contents ?? "")),
        );
      } catch (e) {
        logErrorMsg(e, "Error parsing SQL Compiled Query contents");
        return null;
      }
    })
    .filter(notEmpty);
}

export function getDynamicValuesFromOutputs(
  outputs: UseCellDataResult[],
): Record<string, FilledDynamicValue | undefined> {
  const dynamicOutputs = outputs
    .flatMap((o) => o.outputFragment.frontendOutputContents)
    .filter(
      (o) =>
        o.mimeType === FILLED_DYNAMIC_VALUE_MIMETYPE ||
        o.mimeType === MAP_FILLED_DYNAMIC_VALUE_MIMETYPE,
    );

  return parseFilledDynamicValues(dynamicOutputs, {
    onError: (e) => {
      // gracefully handle errors so we don't crash the page
      console.error(e);
    },
    onJson5Fallback: (contentsLength) => {
      if (contentsLength > 500_000) {
        // Log instances of parsing DFs with NaNs over 500KB so we can see how common this issue is,
        // if it happens often we'll want to look at a faster parsing method here
        logInfoMsg("Possible slow JSON5 parsing of data", {
          safe: { contentsLength },
        });
      }
    },
  });
}

export const getTraceback = (
  outputs: { outputFragment: OutputMPWithResolvedContent }[],
): { traceback: string; outputId: string } | undefined => {
  for (const output of outputs) {
    const { frontendOutputContents: contents, outputType: type } =
      output.outputFragment;
    if (type == null || type !== OutputType.ERROR) {
      continue;
    }
    const tracebackOutput = contents.find(
      (content) =>
        content.mimeType === "traceback" ||
        content.mimeType === CUSTOM_ERROR_MIMETYPE,
    );
    if (tracebackOutput != null) {
      let traceback = JSON.parse(tracebackOutput.contents ?? "");
      if (tracebackOutput.mimeType === CUSTOM_ERROR_MIMETYPE) {
        traceback = JSON.parse(traceback).traceback;
      }
      return {
        traceback,
        outputId: output.outputFragment.id,
      };
    }
  }
};

export const getTableContents = (
  richOutputs: UseCellDataResult[],
): {
  finished: boolean;
  loadedRows: number;
  loadedBytes: null;
  numColumns: number;
  outputId: OutputId;
} | null => {
  const stringContents = richOutputs
    .flatMap((o) => o.outputFragment.frontendOutputContents)
    .find((c) => c.mimeType === DISPLAY_TABLE_MIMETYPE)?.contents;

  if (stringContents != null) {
    let parsedTableOutput: unknown;
    try {
      parsedTableOutput = JSON.parse(JSON.parse(stringContents));
    } catch (err) {
      console.error(err);
      return null;
    }
    if (DisplayTableOutput.guard(parsedTableOutput)) {
      return {
        finished: true,
        loadedRows: parsedTableOutput.rowCount,
        loadedBytes: null,
        numColumns: parsedTableOutput.columns.length,
        outputId: richOutputs[0].outputFragment.id,
      };
    }
  }

  return null;
};

export type SqlStatusWithOutputId = SqlStatus & { outputId: OutputId };
export const useSqlStatus = (
  metadataOutputs: UseCellDataResult[],
  outputs?: UseCellDataResult[],
): SqlStatusWithOutputId | null => {
  return useMemo(() => {
    const mostRecentSqlStatus = getMostRecentMetadataOutputContent(
      metadataOutputs.map(({ outputFragment }) => outputFragment),
      SQL_STATUS_MIMETYPE,
    );
    try {
      if (mostRecentSqlStatus) {
        return {
          ...SqlStatus.check(
            JSON.parse(JSON.parse(mostRecentSqlStatus.contents ?? "")),
          ),
          outputId: mostRecentSqlStatus.outputId,
        };
      }

      // When in unidf query mode, the SQL status is not output, but we can
      // still try to get the parts of the query result metadata such as the
      // row count from the table output
      if (outputs) {
        const { tableOutputs } = getTableOutputs(outputs);
        return getTableContents(tableOutputs);
      }
    } catch (e) {
      logErrorMsg(e, "Error parsing SQL Status contents");
    }
    return null;
  }, [metadataOutputs, outputs]);
};

export const useDistinctColumnValues = (
  outputs: UseCellDataResult[],
): DistinctColumnValues | null => {
  return useMemo(() => getDistinctColumnValues(outputs), [outputs]);
};

export const getDistinctColumnValues = (
  outputs: UseCellDataResult[],
): DistinctColumnValues | null => {
  const dynamicValue =
    getDynamicValuesFromOutputs(outputs)[
      `${HEX_PRIVATE_PREFIX}distinct_column_values`
    ];
  if (dynamicValue && DistinctColumnValues.guard(dynamicValue.value)) {
    return dynamicValue.value;
  }
  return null;
};
